/**
 * ThemedHomePage
 * Renders theme sections dynamically based on store's theme configuration
 *
 * OPTIMIZATION: themeConfig is now passed as a prop from HomePage
 * to eliminate double-fetch and double-preloader issues.
 */

import React from 'react';
import { sectionRegistry } from '../../components/theme-sections';
import ThemeStyleProvider, { mergeStyles } from '../../components/theme-sections/ThemeStyleProvider';
import StorefrontCampaignHero from '../../components/storefront/StorefrontCampaignHero';
import { getThemeRenderer } from '../../themes/themeRendererRegistry';
import './ThemedHomePage.css';

const HERO_SECTION_TYPES = new Set(['hero-banner', 'hero-slider']);

function getOptionalSectionPreference(section, display) {
  if (section.type === 'categories-grid') {
    return display.showCategories === true;
  }

  if (section.type === 'testimonials') {
    return display.showReviews === true;
  }

  if (section.type === 'featured-products') {
    const label = `${section.content?.title || ''} ${section.content?.subtitle || ''}`.toLowerCase();

    if (/\b(new|latest|arrival|arrivals|fresh)\b/.test(label)) {
      return display.showNewArrivals === true;
    }

    if (/\b(trending|trend|hot)\b/.test(label)) {
      return display.showTrending === true;
    }

    if (/\b(best|seller|sellers|popular|favorite|favourite|loved|special|signature|featured)\b/.test(label)) {
      return display.showBestSellers === true;
    }

    return false;
  }

  return true;
}

function ThemedHomePage({ store, themeConfig, campaigns = [] }) {
  // Config is now passed as prop - no need to fetch again
  // This eliminates the double preloader issue

  const { theme, globalOverrides, pages } = themeConfig || {};
  const globalStyles = mergeStyles(theme?.globalSettings, globalOverrides);
  const industry = theme?.industry || 'DEFAULT';
  const dedicatedRenderer = getThemeRenderer(theme);

  // Get home page sections
  const homePageConfig = pages?.find(p => p.pageType === 'HOME');

  // Use page sections if available, then fall back to theme's default sections
  // Page sections take priority (they may have merchant customizations)
  const sections = (homePageConfig?.sections?.length > 0)
    ? homePageConfig.sections
    : (theme?.sections || []);
  const storefrontDisplay = store?.settings?.storefrontDisplay || {};
  const visibleSections = sections.filter(section => getOptionalSectionPreference(section, storefrontDisplay));
  const activeHeroCampaign = campaigns.find(campaign => campaign?.heroEnabled);
  const firstHeroIndex = activeHeroCampaign
    ? visibleSections.findIndex(section => HERO_SECTION_TYPES.has(section.type))
    : -1;

  // No theme config - return null to use legacy HomePage.
  if (!themeConfig) {
    return null;
  }

  // New collection themes own their page composition. Existing marketplace
  // themes retain the section-registry path below, unchanged.
  if (dedicatedRenderer?.HomePage) {
    const DedicatedHomePage = dedicatedRenderer.HomePage;
    return (
      <ThemeStyleProvider styles={globalStyles}>
        <DedicatedHomePage store={store} themeConfig={themeConfig} campaigns={campaigns} />
      </ThemeStyleProvider>
    );
  }

  return (
    <ThemeStyleProvider styles={globalStyles}>
      <div className={`themed-home themed-home--${industry.toLowerCase()}`}>
        {activeHeroCampaign && firstHeroIndex === -1 && (
          <StorefrontCampaignHero campaign={activeHeroCampaign} store={store} />
        )}

        {visibleSections.map((section, index) => {
          if (activeHeroCampaign && index === firstHeroIndex) {
            return (
              <StorefrontCampaignHero
                key={`campaign-hero-${activeHeroCampaign.id}`}
                campaign={activeHeroCampaign}
                store={store}
              />
            );
          }

          const SectionComponent = sectionRegistry[section.type];

          if (!SectionComponent) {
            console.warn(`Unknown section type: ${section.type}`);
            return null;
          }

          return (
            <SectionComponent
              key={section.id}
              settings={section.settings || {}}
              content={section.content || {}}
              globalStyles={globalStyles}
              store={store}
              industry={industry}
            />
          );
        })}

        {sections.length === 0 && (
          <div className="themed-home-empty">
            <p>No sections configured for this theme</p>
          </div>
        )}

      </div>
    </ThemeStyleProvider>
  );
}

/**
 * Check if store has a theme configured
 */
export async function hasThemeConfig(storeSlug) {
  try {
    const res = await fetch(`/api/storefront/${storeSlug}/theme`);
    const data = await res.json();
    return data.success && data.themeConfig?.themeId;
  } catch {
    return false;
  }
}

export default ThemedHomePage;
